import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./RollingRate.scss";

const DIGIT_HEIGHT = 23;

const RollingRate = ({
  oldValue,
  newValue,
  prefix = "",
  suffix = "",
  triggerAnimation = false,
}) => {
  const [animate, setAnimate] = useState(false);
  const [digitHeight, setDigitHeight] = useState(DIGIT_HEIGHT);
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const measure = () => {
      if (!containerRef.current) return;
      const lh = parseFloat(window.getComputedStyle(containerRef.current).lineHeight);
      if (lh > 0) {
        const dpr = window.devicePixelRatio || 1;
        const snapped = Math.round(lh * dpr) / dpr;
        containerRef.current.style.setProperty('--digit-h', `${snapped}px`);
        setDigitHeight(snapped);
      }
    };

    measure();

    let rafId;
    const onResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };

    const vp = window.visualViewport || window;
    vp.addEventListener('resize', onResize);
    return () => {
      vp.removeEventListener('resize', onResize);
      cancelAnimationFrame(rafId);
    };
  }, []);

  // Only start observing after triggerAnimation is true.
  // Animation fires when element scrolls into view after user confirms.
  useEffect(() => {
    if (!triggerAnimation) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setAnimate(true), 200);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [triggerAnimation]);

  // Handle locale-formatted strings (e.g. "1,234.56" from getDecimalFormattedNumber)
  const formatParts = (num) => {
    const clean = String(num).replace(/,/g, "");
    const parsed = parseFloat(clean);
    const [int, dec] = (isNaN(parsed) ? 0 : parsed).toFixed(2).split(".");
    return { int, dec };
  };

  const oldParts = formatParts(oldValue);
  const newParts = formatParts(newValue);

  const maxIntLength = Math.max(
    oldParts.int.length,
    newParts.int.length
  );

  const oldInt = oldParts.int.padStart(maxIntLength, "0");
  const newInt = newParts.int.padStart(maxIntLength, "0");

  const oldDec = oldParts.dec;
  const newDec = newParts.dec;

  // Compute shortest rolling path between two digits, minimum 3 steps for visibility
  const getSequence = (start, end) => {
    if (start === end) return [start];

    const forward = (end - start + 10) % 10;
    const backward = (start - end + 10) % 10;

    let steps, direction;

    if (forward <= backward) {
      steps = forward;
      direction = 1;
    } else {
      steps = backward;
      direction = -1;
    }

    if (steps < 3) steps += 10;

    const seq = [];
    let current = start;

    for (let i = 0; i <= steps; i++) {
      seq.push(current);
      current = (current + direction + 10) % 10;
    }

    return seq;
  };

  const renderDigit = (oldDigit, newDigit, index, offset = 0) => {
    const sequence = animate
      ? getSequence(oldDigit, newDigit)
      : [oldDigit];

    return (
      <span className="digit-container" key={index}>
        <span
          className="digit-strip"
          style={{
            transform: `translateY(-${
              (sequence.length - 1) * digitHeight
            }px)`,
            transition: animate
              ? `transform ${
                  600 + sequence.length * 80
                }ms cubic-bezier(0.22, 1, 0.36, 1) ${
                  (index + offset) * 80
                }ms`
              : "none",
          }}
        >
          {sequence.map((num, i) => (
            <span key={i} className="digit">
              {num}
            </span>
          ))}
        </span>
      </span>
    );
  };

  return (
    <span className="rate-roll" ref={containerRef}>
      {prefix && <span className="affix">{prefix}&nbsp;</span>}

      {/* INTEGER */}
      {newInt.split("").map((char, i) =>
        renderDigit(Number(oldInt[i]), Number(char), i)
      )}

      {/* DOT + DECIMAL */}
      <span className="decimal">.</span>
      {newDec.split("").map((char, i) =>
        renderDigit(
          Number(oldDec[i]),
          Number(char),
          i,
          newInt.length
        )
      )}

      {suffix && <span className="affix">{suffix}</span>}
    </span>
  );
};

export default RollingRate;